import config from 'config'
import store from 'config/store'
import { ActiveCampaign } from 'modules/campaigns/types'
import { getEmbedLocale } from 'modules/embed/utils'
import { getIntegrationIdentityContext } from 'modules/identity'
import { isEmpty, isNil, minBy,pathOr, reduce } from 'ramda'
import { CampaignDisplayType } from 'types/campaigns'
import { Conversation } from 'types/conversation'
import { EmbedConfiguration, TeamUser } from 'types/embed'
import { Message, MessageAttributes, MessageAuthorType, MessageContentType, MessageType, PreMessage } from 'types/message'
import { uuid } from 'utils'

const DEFAULT_ATTRIBUTES: MessageAttributes = {
  widgetVersion: 2,
  widgetRelease: config.ENV !== 'LOCAL' ? `___RELEASE_ID` : 'local'
}

/**
 * Resolves auto-assignee id for the created message in this order
 * 1) If an auto-assignee is provided from the embed via drift.config({...})
 * 2) If we have an assignedAgentId from a oneshot-token (i.e. email campaign)
 * 3) If we're enrolled in an ABM playbook
 */
const resolveAutoAssigneeId = () => {
  const state = store.getState()
  const { assignedAgentId } = state.session
  const { autoAssigneeId: autoAssigneeIdFromEmbed } = pathOr<Partial<EmbedConfiguration>>({}, ['embed', 'configuration'], state)

  const activeCampaign = pathOr(null, ['campaigns', 'activeCampaign'], state)
  const hasAutomaticSender = pathOr(false, ['attributes', 'automaticSender'], activeCampaign)

  if (autoAssigneeIdFromEmbed) {
    return autoAssigneeIdFromEmbed
  }

  if(assignedAgentId) {
    return assignedAgentId
  }

  if (hasAutomaticSender) {
    const activeCampaignAuthorId = pathOr(undefined, ['attributes', 'senderId'], activeCampaign)
    return activeCampaignAuthorId
  }

  return undefined
}

const attachRoutingRulesForMessage = () => {
  const routingAttributes = {}

  const { conversations = {} } = store.getState()
  const routingRules = pathOr([], ['matchedRoutingRules'], conversations)

  const routingRuleIds = routingRules.map<number>(rule => rule.id)
  const highestPriorityRoutingRule = !!routingRules.length ?
    reduce(minBy((rule: any) => rule.priority), routingRules[0], routingRules) :
    null

  if (highestPriorityRoutingRule && !!highestPriorityRoutingRule.id) {
    routingAttributes['targetingRuleId'] = highestPriorityRoutingRule.id
  }

  if (routingRuleIds && !!routingRuleIds.length) {
    routingAttributes['targetingRuleIds'] = routingRuleIds
  }

  return routingAttributes
}

const attachCampaignAttributesForMessage = () => {
  const campaignAttributes = {}
  const { campaigns = {} } = store.getState()
  const { activeCampaign = {}, campaignEvalId } = campaigns

  if (activeCampaign.id) {
    campaignAttributes['campaignId'] = activeCampaign.id
    campaignAttributes['relatedCampaignId'] = activeCampaign.id
    campaignAttributes['isExpandable'] = true
  }

  if (campaignEvalId) {
    campaignAttributes['targetingEvalUUID'] = campaignEvalId
  }

  return campaignAttributes
}

const hydrateContextAttributesForMessage = (): MessageAttributes => {
  return {
    locale: getEmbedLocale(),
    ...attachRoutingRulesForMessage(),
    ...attachCampaignAttributesForMessage(),
    integrations: getIntegrationIdentityContext()
  }
}

const _applyPremessagesForMessage = (activeConversation?: any, activeCampaign?: ActiveCampaign): PreMessage[] => {
  const {
    profileMode = false,
    team,
    theme = {}
  } = pathOr<Partial<EmbedConfiguration>>({}, ['embed', 'configuration'], store.getState())

  const autoAssigneeId = resolveAutoAssigneeId()

  const isWelcomeMessageCampaign = pathOr(null, ['attributes', 'campaignDisplayType'], activeCampaign) === CampaignDisplayType.WELCOME_MESSAGE
  const activeCampaignBody = pathOr<string>('', ['attributes', 'message'], activeCampaign)
  const activeCampaignAuthorId = pathOr(null, ['attributes', 'senderId'], activeCampaign)

  const preMessages: PreMessage[] = []
  const isFirstMessageInConversation = !activeConversation

  if (!isFirstMessageInConversation) { return preMessages }

  if (profileMode && !!autoAssigneeId) {
    preMessages.push({
      body: theme.welcomeMessage,
      sender: team.find(user => user.id === autoAssigneeId)
    })
  }

  if (isWelcomeMessageCampaign) {
    const activeCampaignAuthor = autoAssigneeId || activeCampaignAuthorId

    preMessages.push({
      body: activeCampaignBody,
      sender: team.find(user => user.id === activeCampaignAuthor)
    })
  }

  return preMessages
}

interface CreateMessageParams {
  message?: string,
  activeConversation?: any,
  attributes?: MessageAttributes,
  isMetaMessage?: boolean,
  authorId?: number,
  status?: string,
  attachments?: any,
  context?: Record<string, any>,
  preMessages?: PreMessage[],
  inboxId?: number,
  type?: MessageType,
  contentType?: MessageContentType,

  isEndUserMessage?: boolean
}

export const createMessage = ({
  message = '',
  activeConversation,
  attributes = {},
  isMetaMessage = false,
  authorId,
  status,
  attachments = [],
  preMessages: preMessagesArg = [],
  inboxId: inboxIdArg,
  type = MessageType.CHAT,
  contentType = MessageContentType.CHAT,
  isEndUserMessage = true
}: CreateMessageParams): Partial<Message> => {
  const state = store.getState()
  const session: { context: Record<string, any> } = pathOr({ context: {} }, ['session'], state)
  const authorType = pathOr<MessageAuthorType>(MessageAuthorType.LEAD, ['session', 'endUser', 'type'], state)
  const activeCampaign = pathOr(null, ['campaigns', 'activeCampaign'], state)
  const { profileMode = false }: Partial<EmbedConfiguration> = pathOr({}, ['embed', 'configuration'], state)

  const inboxId = resolveInboxIdForMessage(inboxIdArg)
  const preMessages = (
    isNil(preMessagesArg) || isEmpty(preMessagesArg) ?
      _applyPremessagesForMessage(activeConversation, activeCampaign) :
      preMessagesArg
  )

  const contextAttributes = hydrateContextAttributesForMessage()
  const widgetGuid = isEndUserMessage ? uuid() : undefined

  return {
    attachments,
    conversationId: activeConversation,
    body: message,
    type,
    status,
    contentType,
    authorId,
    authorType,
    inboxId,
    attributes:{
      ...attributes,
      widgetGuid,
      isMetaMessage,
      generatedAt: Date.now().valueOf(),
      autoAssigneeId: resolveAutoAssigneeId(),
      profileMode,
      preMessages,
      isFirstMessage: !activeConversation,
      ...DEFAULT_ATTRIBUTES,
      ...contextAttributes
    },
    context: {
      userAgent: session.context.userAgent,
      timezone: session.context.timezone,
      locale: session.context.locale,
      page: session.context.page
    }
  }
}

export const createPreMessagesForCampaign = (campaignMessage: Message): PreMessage[] => {
  const {
    body: campaignBody,
    attributes: {
      title: campaignTitle = null,
      senderId = null,
      noSenderId = false,
      automaticSender = false,
      campaignDisplayType = null
    } = {}
  } = campaignMessage
  const isWelcomeMessage = campaignDisplayType === CampaignDisplayType.WELCOME_MESSAGE
  const sender = getSender(senderId, noSenderId, automaticSender)

  const preMessages: PreMessage[] = []
  if (campaignTitle && !isWelcomeMessage) {
    preMessages.push({
      id: uuid(),
      body: campaignTitle,
      sender
    })
  }
  if (campaignBody) {
    preMessages.push({
      id: uuid(),
      body: campaignBody,
      sender
    })
  }

  return preMessages
}

const getSender = (senderId: number, noSenderId: boolean = false, useAutomaticSender: boolean = false): TeamUser => {
  if (noSenderId) {
    return null
  }

  const { autoAssigneeId: autoAssigneeIdFromEmbed, team }: Partial<EmbedConfiguration> = pathOr({}, ['embed', 'configuration'], store.getState())
  const { assignedAgentId } = store.getState().session

  const autoAssigneeId = autoAssigneeIdFromEmbed || assignedAgentId

  if (useAutomaticSender && autoAssigneeId) {
    return team.find(user => user.id === autoAssigneeId)
  }

  return team.find(user => user.id === senderId)
}


/**
 * Handles resolution of the inboxId to be used for a given mesasge to be sent, determined as follows:
 * 1) If there's an inboxId passed into the embed config via drift.config({ inboxId: 12345 }) it takes priority
 * 2) If there's a defined inbox for a conversation already being had (would be the case if the conversation is being continued or if created from the websocket)
 * 3) If there's an an inboxId on the active campaign, use that (in the case of pre-rendered announcements as we pull inboxId from the embed campaign object)
 * 4) Use the default inboxId for the org as defined in their settings
 * @param inboxId - The inbox in the conversation view the message should go to
 */
const resolveInboxIdForMessage = (inboxId: number): number => {
  const state = store.getState()
  const { inboxId: defaultInboxId }: { inboxId?: number } = pathOr<Partial<EmbedConfiguration>>({}, ['embed', 'configuration'], state)

  if (!isNil(inboxId)) {
    return inboxId
  }

  const activeConversationId = pathOr<number>(null, ['conversations', 'activeConversation'], state)
  const activeConversation = pathOr<Conversation>(null, ['conversations', 'conversations', activeConversationId, 'conversation'], state)

  if(!isNil(activeConversation)) {
    return activeConversation.inboxId
  }

  const activeCampaignInboxId = pathOr<number>(null, ['campaigns', 'activeCampaign', 'inboxId'], state)
  if (!isNil(activeCampaignInboxId)) {
    return activeCampaignInboxId
  }

  return defaultInboxId
}
